// Report.jsx
import React, { useEffect, useState } from 'react';
import { apiCall } from '../../utils/api';
import toast from 'react-hot-toast';
import PageHeader from '../../components/ui/page-header';
import Button, { IconButton } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Label, Input, Select } from '../../components/ui/field';
import { Table, THead, TBody, Th, Tr } from '../../components/ui/table';
import { FaSearch, FaTimes } from 'react-icons/fa';
import { LuSlidersHorizontal } from 'react-icons/lu';
import { useLocation } from 'react-router-dom';

// Order type options
const ORDER_TYPES = [
    { id: '1', name: 'Payment - 1' },
    { id: '2', name: 'Recharge & DTH - 2' },
    { id: '3', name: 'Bill Payment - 3' },
    { id: '4', name: 'Bonus - 4' },
    { id: '5', name: 'Manual Debit - 5' },
    { id: '6', name: 'Manual Add - 6' },
    { id: '7', name: 'Debit Adjustment - 7' },
    { id: '8', name: 'Credit Adjustment - 8' },
    { id: '9', name: 'Payout - 9' },
    { id: '10', name: 'SMS Charges - 10' },
    { id: '11', name: 'Payment Refund - 11' },
    { id: '12', name: 'Service Comission - 12' },
    { id: '13', name: 'Service Commission Reverse - 13' },
    { id: '14', name: 'Service Refund - 14' },
    { id: '15', name: 'PG Fees - 15' },
    { id: '16', name: 'Transfer Debit - 16' },
    { id: '17', name: 'Transfer Credit - 17' },
    { id: '18', name: 'Agent Commission - 18' }
];

// Tabs config
const TABS = [
    { id: 'orderType', label: 'Order Type' },
    { id: 'providerWise', label: 'Provider Wise' },
    { id: 'operatorWise', label: 'Operator Wise' }
];

// Numeric fields used for totals
const TOTAL_FIELDS = [
    'amount',
    'successAmount',
    'failedAmount',
    'initiatedAmount',
    'pendingAmount',
    'refundAmount',
    'userProfit',
    'agentProfit',
    'adminProfit'
];

function Report() {
    const today = new Date().toISOString().split('T')[0];

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const retailerNumber = params.get("retailerNumber");

    // Filter states
    const [filters, setFilters] = useState({
        retailerNumber: retailerNumber || '',
        fromDate: today,
        toDate: today,
        provider: '',
        operator: '',
        orderType: ''
    });

    // Dropdown data
    const [providers, setProviders] = useState([{ id: '', name: 'All Providers' }]);
    const [operators, setOperators] = useState([{ id: '', name: 'All Operators', logo: '' }]);
    const [selectedOperatorName, setSelectedOperatorName] = useState('');

    // Operator modal
    const [isOperatorModalOpen, setIsOperatorModalOpen] = useState(false);
    const [operatorModalSearch, setOperatorModalSearch] = useState('');
    const [operatorModalList, setOperatorModalList] = useState([]);
    const [operatorModalLoading, setOperatorModalLoading] = useState(false);

    // Tabs + results
    const [activeTab, setActiveTab] = useState('orderType');
    const [reportData, setReportData] = useState([]);
    const [loading, setLoading] = useState(false);

    // Filter form expand on mobile
    const [showFilters, setShowFilters] = useState(false);

    /* ---------------- Data fetchers ---------------- */

    // Providers — no category coupling
    const fetchProviders = async () => {
        try {
            const result = await apiCall('provider', { category: '' }, true);
            if (result.status === true) {
                const list = [
                    { id: '', name: 'All Providers' },
                    ...result.provider.map(p => ({ id: p.id, name: p.name }))
                ];
                setProviders(list);
                return list;
            } else {
                toast.error(result.message);
                const defaultList = [{ id: '', name: 'All Providers' }];
                setProviders(defaultList);
                return defaultList;
            }
        } catch (e) {
            toast.error('Network error. Please try again.');
            const defaultList = [{ id: '', name: 'All Providers' }];
            setProviders(defaultList);
            return defaultList;
        }
    };

    // Operators — no category coupling, no sub_category
    const fetchOperators = async (typing = '') => {
        try {
            const result = await apiCall(
                'operators',
                {
                    typing: typing,
                    category: ''
                },
                true
            );

            if (result.status === true) {
                const operatorList = [
                    { id: '', name: 'All Operators', logo: '' },
                    ...result.operators.map(operator => ({
                        id: operator.operator_id,
                        name: operator.biller_name,
                        logo: operator.logo
                    }))
                ];
                setOperators(operatorList);
                return operatorList;
            } else {
                const defaultList = [{ id: '', name: 'All Operators', logo: '' }];
                setOperators(defaultList);
                return defaultList;
            }
        } catch (error) {
            const defaultList = [{ id: '', name: 'All Operators', logo: '' }];
            setOperators(defaultList);
            return defaultList;
        }
    };

    // Load providers & operators once on mount
    useEffect(() => {
        fetchProviders();
        fetchOperators('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Operator modal helpers
    const openOperatorModal = async () => {
        setIsOperatorModalOpen(true);
        setOperatorModalSearch('');
        setOperatorModalLoading(true);

        const result = await fetchOperators('');
        setOperatorModalList(result || []);
        setOperatorModalLoading(false);
    };

    const closeOperatorModal = () => {
        setIsOperatorModalOpen(false);
        setOperatorModalSearch('');
    };

    const handleOperatorModalSearch = async (value) => {
        setOperatorModalSearch(value);
        setOperatorModalLoading(true);

        const result = await fetchOperators(value);
        setOperatorModalList(result || []);
        setOperatorModalLoading(false);
    };

    const selectOperatorFromModal = (operator) => {
        handleFilterChange('operator', operator.id);
        setSelectedOperatorName(operator.name);
        closeOperatorModal();
    };

    const clearOperator = () => {
        handleFilterChange('operator', '');
        setSelectedOperatorName('');
    };

    /* ---------------- Report API ---------------- */

    const getReportConfig = (tabId) => {
        switch (tabId) {
            case 'providerWise':
                return { action: 'report-provider-wise' };
            case 'operatorWise':
                return { action: 'report-operator-wise' };
            case 'orderType':
            default:
                return { action: 'report-order-type' };
        }
    };

    const fetchReport = async (tab = activeTab) => {
        setLoading(true);
        try {
            const { action } = getReportConfig(tab);
            const params = {
                retailerNumber: filters.retailerNumber,
                fromDate: filters.fromDate,
                toDate: filters.toDate,
                provider: filters.provider || '',
                operator: filters.operator || '',
                orderType: filters.orderType || ''
            };

            const result = await apiCall(action, params, true);

            if (result.status === true) {
                setReportData(result.reports || result.data || result.report || []);
            } else {
                toast.error(result.message || 'Failed to fetch report');
                setReportData([]);
            }
        } catch (e) {
            toast.error('Network error. Please try again.');
            setReportData([]);
        } finally {
            setLoading(false);
        }
    };

    // Initial auto-call for default active tab
    useEffect(() => {
        fetchReport('orderType');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ---------------- Handlers ---------------- */

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const applyFilters = () => {
        fetchReport(activeTab);
    };

    const resetFilters = () => {
        setFilters({
            retailerNumber: '',
            fromDate: today,
            toDate: today,
            provider: '',
            operator: '',
            orderType: ''
        });
        setSelectedOperatorName('');
        fetchReport(activeTab);
    };

    const handleTabClick = (tabId) => {
        setActiveTab(tabId);
        fetchReport(tabId); // each click calls API
    };

    /* ---------------- Totals helpers ---------------- */

    const computeTotals = (rows) => {
        const totals = {};
        TOTAL_FIELDS.forEach((field) => {
            totals[field] = rows.reduce((sum, row) => sum + Number(row[field] || 0), 0);
        });
        return totals;
    };

    const renderTotalsRow = (label) => {
        const totals = computeTotals(reportData);
        return (
            <tr className="bg-slate-100 dark:bg-slate-800/70 font-semibold border-t-2 border-slate-300 dark:border-slate-600">
                <td className="px-4 py-3 text-sm font-bold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                    {label}
                </td>
                <td className="px-4 py-3 text-sm font-bold">
                    ₹{totals.amount.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    ₹{totals.successAmount.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-rose-600 dark:text-rose-400">
                    ₹{totals.failedAmount.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-slate-600 dark:text-slate-300">
                    ₹{totals.initiatedAmount.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-amber-600 dark:text-amber-400">
                    ₹{totals.pendingAmount.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-slate-600 dark:text-slate-300">
                    ₹{totals.refundAmount.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-bold">
                    ₹{totals.userProfit.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-bold">
                    ₹{totals.agentProfit.toFixed(2)}
                </td>
                <td className="px-4 py-3 text-sm font-bold text-emerald-600 dark:text-emerald-400">
                    ₹{totals.adminProfit.toFixed(2)}
                </td>
            </tr>
        );
    };

    /* ---------------- Table renderers ---------------- */

    const renderOrderTypeTable = () => (
        <Table>
            <THead>
                <tr>
                    <Th>Order Type</Th>
                    <Th>Total Amount (₹)</Th>
                    <Th>Success (₹)</Th>
                    <Th>Failed (₹)</Th>
                    <Th>Initiated (₹)</Th>
                    <Th>Pending (₹)</Th>
                    <Th>Refund (₹)</Th>
                    <Th>User Profit (₹)</Th>
                    <Th>Agent Profit (₹)</Th>
                    <Th>Admin Profit (₹)</Th>
                </tr>
            </THead>
            <TBody>
                {reportData.map((row, i) => (
                    <Tr key={row.orderTypeId ?? i}>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">
                            {row.orderType}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold">
                            ₹{Number(row.amount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-emerald-600 dark:text-emerald-400">
                            ₹{Number(row.successAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-rose-600 dark:text-rose-400">
                            ₹{Number(row.failedAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                            ₹{Number(row.initiatedAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-amber-600 dark:text-amber-400">
                            ₹{Number(row.pendingAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                            ₹{Number(row.refundAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                            ₹{Number(row.userProfit || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                            ₹{Number(row.agentProfit || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                            ₹{Number(row.adminProfit || 0).toFixed(2)}
                        </td>
                    </Tr>
                ))}
            </TBody>
            <tfoot>
                {renderTotalsRow('Total')}
            </tfoot>
        </Table>
    );

    const renderProviderWiseTable = () => (
        <Table>
            <THead>
                <tr>
                    <Th>Provider</Th>
                    <Th>Total Amount (₹)</Th>
                    <Th>Success (₹)</Th>
                    <Th>Failed (₹)</Th>
                    <Th>Initiated (₹)</Th>
                    <Th>Pending (₹)</Th>
                    <Th>Refund (₹)</Th>
                    <Th>User Profit (₹)</Th>
                    <Th>Agent Profit (₹)</Th>
                    <Th>Admin Profit (₹)</Th>
                </tr>
            </THead>
            <TBody>
                {reportData.map((row, i) => (
                    <Tr key={i}>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100 whitespace-nowrap">
                            {row.providerName || (
                                <span className="text-slate-400 dark:text-slate-500 italic">Unassigned</span>
                            )}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold">
                            ₹{Number(row.amount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-emerald-600 dark:text-emerald-400">
                            ₹{Number(row.successAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-rose-600 dark:text-rose-400">
                            ₹{Number(row.failedAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                            ₹{Number(row.initiatedAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-amber-600 dark:text-amber-400">
                            ₹{Number(row.pendingAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                            ₹{Number(row.refundAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                            ₹{Number(row.userProfit || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                            ₹{Number(row.agentProfit || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                            ₹{Number(row.adminProfit || 0).toFixed(2)}
                        </td>
                    </Tr>
                ))}
            </TBody>
            <tfoot>
                {renderTotalsRow('Total')}
            </tfoot>
        </Table>
    );

    const renderOperatorWiseTable = () => (
        <Table>
            <THead>
                <tr>
                    <Th>Operator</Th>
                    <Th>Total Amount (₹)</Th>
                    <Th>Success (₹)</Th>
                    <Th>Failed (₹)</Th>
                    <Th>Initiated (₹)</Th>
                    <Th>Pending (₹)</Th>
                    <Th>Refund (₹)</Th>
                    <Th>User Profit (₹)</Th>
                    <Th>Agent Profit (₹)</Th>
                    <Th>Admin Profit (₹)</Th>
                </tr>
            </THead>
            <TBody>
                {reportData.map((row, i) => (
                    <Tr key={i}>
                        <td className="px-4 py-3 text-sm font-medium text-slate-900 dark:text-slate-100">
                            {row.operatorName || (
                                <span className="text-slate-400 dark:text-slate-500 italic">Unassigned</span>
                            )}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold">
                            ₹{Number(row.amount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-emerald-600 dark:text-emerald-400">
                            ₹{Number(row.successAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-rose-600 dark:text-rose-400">
                            ₹{Number(row.failedAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                            ₹{Number(row.initiatedAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-amber-600 dark:text-amber-400">
                            ₹{Number(row.pendingAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                            ₹{Number(row.refundAmount || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                            ₹{Number(row.userProfit || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm">
                            ₹{Number(row.agentProfit || 0).toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                            ₹{Number(row.adminProfit || 0).toFixed(2)}
                        </td>
                    </Tr>
                ))}
            </TBody>
            <tfoot>
                {renderTotalsRow('Total')}
            </tfoot>
        </Table>
    );

    const renderActiveTable = () => {
        if (!reportData.length) {
            return (
                <div className="px-4 py-8 text-center text-slate-500 dark:text-slate-400">
                    <p className="text-sm">No report data found</p>
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">Try adjusting your filters</p>
                </div>
            );
        }
        if (activeTab === 'providerWise') return renderProviderWiseTable();
        if (activeTab === 'operatorWise') return renderOperatorWiseTable();
        return renderOrderTypeTable();
    };

    /* ---------------- Render ---------------- */

    return (
        <>
            <PageHeader title="Reports" description="Generate and view transaction reports">
                <Button
                    variant="secondary"
                    icon={LuSlidersHorizontal}
                    className="lg:hidden"
                    onClick={() => setShowFilters((open) => !open)}
                >
                    {showFilters ? 'Hide filters' : 'Filters'}
                </Button>
            </PageHeader>

            {/* Filter Form */}
            <Card className={`mb-6 overflow-hidden ${showFilters ? '' : 'hidden lg:block'}`}>
                <div className="p-4 sm:p-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                        {/* Retailer Number */}
                        <div>
                            <Label>Retailer Number</Label>
                            <Input
                                type="text"
                                value={filters.retailerNumber}
                                onChange={(e) => handleFilterChange('retailerNumber', e.target.value)}
                                placeholder="Enter retailer number..."
                                className="w-full px-3 py-2"
                            />
                        </div>

                        {/* From / To date */}
                        <div>
                            <Label>Date Range</Label>
                            <div className="flex flex-wrap gap-2">
                                <Input
                                    type="date"
                                    value={filters.fromDate}
                                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                                    className="min-w-[7rem] flex-1 px-3 py-2"
                                />
                                <Input
                                    type="date"
                                    value={filters.toDate}
                                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                                    className="min-w-[7rem] flex-1 px-3 py-2"
                                />
                            </div>
                        </div>

                        {/* Provider — always enabled */}
                        <div>
                            <Label>Provider</Label>
                            <Select
                                value={filters.provider}
                                onChange={(e) => handleFilterChange('provider', e.target.value)}
                                className="w-full px-3 py-2"
                            >
                                {providers.map(p => (
                                    <option key={p.id} value={p.id}>{p.name}</option>
                                ))}
                            </Select>
                        </div>

                        {/* Operator — always enabled */}
                        <div>
                            <Label>Operator</Label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={selectedOperatorName}
                                    readOnly
                                    placeholder="Click to search operator..."
                                    className="flex-1 px-3 py-2 border rounded-lg text-sm border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 cursor-pointer"
                                    onClick={openOperatorModal}
                                />
                                {filters.operator && (
                                    <Button
                                        onClick={clearOperator}
                                        variant="danger-soft" size="icon"
                                        title="Clear operator"
                                    >
                                        <FaTimes />
                                    </Button>
                                )}
                                <Button
                                    onClick={openOperatorModal}
                                    size="icon"
                                >
                                    <FaSearch />
                                </Button>
                            </div>
                        </div>

                        {/* Order Type — last, no longer gates the above */}
                        <div>
                            <Label>Order Type</Label>
                            <Select
                                value={filters.orderType}
                                onChange={(e) => handleFilterChange('orderType', e.target.value)}
                                className="w-full px-3 py-2"
                            >
                                <option value="">All Order Types</option>
                                {ORDER_TYPES.map(o => (
                                    <option key={o.id} value={o.id}>{o.name}</option>
                                ))}
                            </Select>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 mt-4 justify-end">
                        <Button onClick={applyFilters} className="min-w-[100px]">Apply</Button>
                        <Button onClick={resetFilters} variant="secondary" className="min-w-[100px]">Reset</Button>
                    </div>
                </div>
            </Card>

            {/* Tabs */}
            <div className="border-b border-slate-200 dark:border-slate-800 mb-4 overflow-x-auto">
                <nav className="flex gap-1 min-w-max">
                    {TABS.map(tab => {
                        const isActive = activeTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => handleTabClick(tab.id)}
                                className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${isActive
                                    ? 'border-brand-600 text-brand-600 dark:border-brand-400 dark:text-brand-400'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </nav>
            </div>

            {/* Results Count */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                <p className="text-sm text-slate-600 dark:text-slate-300">
                    Showing <span className="font-semibold text-slate-900 dark:text-slate-100">{reportData.length}</span> records
                </p>
                {loading && (
                    <div className="flex items-center text-sm text-slate-500 dark:text-slate-400">
                        <svg className="animate-spin h-4 w-4 mr-2 text-brand-600 dark:text-brand-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Loading...
                    </div>
                )}
            </div>

            {/* Table */}
            <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                    {renderActiveTable()}
                </div>
            </Card>

            {/* Operator Search Modal */}
            {isOperatorModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
                        onClick={closeOperatorModal}
                    ></div>

                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] flex flex-col overflow-hidden">
                            {/* Header - Fixed */}
                            <div className="flex-shrink-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 px-6 py-4 flex justify-between items-center">
                                <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100">Search Operator</h3>
                                <IconButton onClick={closeOperatorModal}>
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </IconButton>
                            </div>

                            {/* Search Input - Fixed */}
                            <div className="flex-shrink-0 px-6 py-4 border-b border-slate-200 dark:border-slate-800">
                                <div className="relative">
                                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                                    <Input
                                        type="text"
                                        value={operatorModalSearch}
                                        onChange={(e) => handleOperatorModalSearch(e.target.value)}
                                        placeholder="Type to search operators..."
                                        className="w-full pl-10 pr-4 py-3"
                                        autoFocus
                                    />
                                </div>
                            </div>

                            {/* Results - Scrollable */}
                            <div className="flex-1 overflow-y-auto p-4 min-h-[200px] max-h-[400px]">
                                {operatorModalLoading ? (
                                    <div className="flex flex-col items-center justify-center h-40">
                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-600"></div>
                                        <p className="mt-4 text-slate-600 dark:text-slate-300 text-sm">Loading operators...</p>
                                    </div>
                                ) : operatorModalList.length > 0 ? (
                                    <div className="space-y-2">
                                        {operatorModalList.map((operator) => (
                                            <div
                                                key={operator.id}
                                                onClick={() => selectOperatorFromModal(operator)}
                                                className={`px-4 py-3 rounded-lg cursor-pointer flex items-center gap-3 transition-colors ${filters.operator === operator.id
                                                    ? 'bg-brand-50 dark:bg-brand-500/10 border-2 border-brand-500'
                                                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/60 border-2 border-transparent'
                                                    }`}
                                            >
                                                {operator.logo && (
                                                    <img
                                                        src={operator.logo}
                                                        alt={operator.name}
                                                        className="w-8 h-8 rounded-full object-contain flex-shrink-0"
                                                        onError={(e) => { e.target.style.display = 'none'; }}
                                                    />
                                                )}
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">{operator.name}</p>
                                                </div>
                                                {filters.operator === operator.id && (
                                                    <span className="text-brand-600 dark:text-brand-400 flex-shrink-0">
                                                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center h-40">
                                        <svg className="w-16 h-16 text-slate-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <p className="text-slate-500 dark:text-slate-400">No operators found</p>
                                        <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Try adjusting your search</p>
                                    </div>
                                )}
                            </div>

                            {/* Footer - Fixed */}
                            <div className="flex-shrink-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-6 py-3 flex justify-between items-center">
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                    {operatorModalList.length > 0 && `${operatorModalList.length} operators found`}
                                </span>
                                <Button onClick={closeOperatorModal} variant="secondary">
                                    Close
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Report;